import { RoutePathsEnum } from "infrastructure/RoutePathsEnum";
import { Page, Spinner } from "@whitespace/whitespace-ui";
import { useNavigate, useParams } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import AmplifyError from "components/AmplifyError/AmplifyError";
import { useStateMachine } from "little-state-machine";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import StaffOnSiteProps from "interfaces/SiteDiary/StaffOnSite/StaffOnSiteProps";
import { updateSiteDiarySelectionDetails } from "actions/updateSiteDiarySelectionDetails";
import { convertToDateFromLocalDate, convertToLocalDate } from "helpers/DateUtils";
import ISiteDiaryViewModel from "interfaces/SiteDiary/ISiteDiaryViewModel";
import AuthorisedVisitorsTemporaryWorkersProps from "interfaces/SiteDiary/AuthorisedTemporaryWorkers/AuthorisedVisitorsTemporaryWorkersProps";
import AuthorisedMaintenanceProps from "interfaces/SiteDiary/AuthorisedMaintenance/AuthorisedMaintenanceProps";
import SiteDiaryItemOrdersProps from "interfaces/SiteDiary/ItemOrder/SiteDiaryItemOrdersProps";
import EventsAndIncidentsProps from "interfaces/SiteDiary/EventIncident/EventsAndIncidentsProps";
import SiteDetails from "./SiteDetails/SiteDetails";
import StaffOnSite from "./StaffOnSite/StaffOnSite";
import AuthorisedVisitorsTemporaryWorkers from "./AuthorisedVisitorsTemporaryWorkers/AuthorisedVisitorsTemporaryWorkers";
import EventsAndIncidents from "./EventsAndIncidents/EventsAndIncidents";
import AuthorisedMaintenance from "./AuthorisedMaintenance/AuthorisedMaintenance";
import SiteDiaryItemOrders from "./SiteDiaryItemOrders/SiteDiaryItemOrders";
import { mapGetSiteDiaryResponseModelToStaffOnSiteProps } from "./StaffOnSite/Mappers/StaffOnSiteDataMapper";
import { mapSiteDiaryToViewModel } from "./Mappers/SiteDiaryToViewModel";
import { mapGetSiteDiaryResponseModelToAuthorisedVisitorsTemporaryWorkers }
  from "./AuthorisedVisitorsTemporaryWorkers/Mappers/AuthorisedVisitorsTemporaryWorkersMapper";
import "./SiteDiaryLandingPage.scss";
import { mapSiteDiaryItemOrdersToSiteDiaryItemOrderViewModels } from "./SiteDiaryItemOrders/Mappers/ItemOrdersMapper";
import { mapGetSiteDiaryResponseModelToAuthorisedMaintenance } from "./AuthorisedMaintenance/Mappers/AuthorisedMaintenanceDataMapper";
import { mapSiteDiaryEventsAndIncidentsToViewModels } from "./EventsAndIncidents/Mappers/EventsAndIncidentsMapper";

const SiteDiaryLandingPage = () => {
  const { hwrcSiteIdQs, diaryDate } = useParams();
  const navigate = useNavigate();
  const emptyStaffOnSite = {
    staffOnSite: [],
    hwrcSiteId: "",
    diaryDate: "",
    diarySaved: false
  };

  const emptyAuthorisedVisitorsTemporaryWorkers : AuthorisedVisitorsTemporaryWorkersProps = {
    authorisedVisitorsTemporaryWorkersDetails: [],
    diarySaved: false
  };

  const emptyAuthorisedMaintenance : AuthorisedMaintenanceProps = {
    authorisedMaintenanceDetails: [],
    hwrcSiteId: "",
    diaryDate: "",
    diarySaved: false
  };

  const emptyItemOrders : SiteDiaryItemOrdersProps = {
    itemOrders: []
  };

  const emptyEventsIncidents: EventsAndIncidentsProps = {
    eventsAndIncidents: []
  };

  const [showExceptions, setShowExceptions] = useState<boolean>(true);
  const [diarySaved, setDiarySaved] = useState<boolean>(false);
  const [currentHwrcSiteId, setCurrentHwrcSiteId] = useState<string>(hwrcSiteIdQs ?? "");
  const [currentDiaryDate, setCurrentDiaryDate] = useState<Date>(diaryDate ? convertToDateFromLocalDate(diaryDate) : new Date());

  const emptySiteDiary : ISiteDiaryViewModel = {
    reportTimes: [],
    submittedReportTimes: [],
    weatherCondition: "",
    isEmpty: true
  };

  const [siteDiary, setSiteDiary] = useState<ISiteDiaryViewModel | undefined>(emptySiteDiary);
  const [staffOnSiteData, setStaffOnSiteData] = useState<StaffOnSiteProps>(emptyStaffOnSite);
  // eslint-disable-next-line max-len
  const [authorisedVisitorsTemporaryWorkersData, setAuthorisedVisitorsTemporaryWorkers] = useState<AuthorisedVisitorsTemporaryWorkersProps>(emptyAuthorisedVisitorsTemporaryWorkers);
  const [authorisedMaintenanceData, setAuthorisedMaintenanceData] = useState<AuthorisedMaintenanceProps>(emptyAuthorisedMaintenance);
  const [itemOrders, setItemOrders] = useState<SiteDiaryItemOrdersProps>(emptyItemOrders);
  const [eventsIncidents, setEventsIncidents] = useState<EventsAndIncidentsProps>(emptyEventsIncidents);

  const { actions } = useStateMachine({
    updateSiteDiarySelectionDetails
  });

  const {
    isSending, errors, data, sendRequest, statusCode
  } = useAmplifyREST();

  useEffect(() => {
    actions.updateSiteDiarySelectionDetails(undefined);
  }, []);

  const diaryLoadHandler = (hwrcSiteId : string, selectedDate : Date) => {
    setSiteDiary(emptySiteDiary);
    setStaffOnSiteData(emptyStaffOnSite);
    setAuthorisedVisitorsTemporaryWorkers(emptyAuthorisedVisitorsTemporaryWorkers);
    setAuthorisedMaintenanceData(emptyAuthorisedMaintenance);
    setEventsIncidents(emptyEventsIncidents);

    if (statusCode !== 400) {
      sendRequest(
        `hwrcsitediary/hwrcsite/${hwrcSiteId}/diary/${convertToLocalDate(selectedDate)}`,
        "GET"
      );
    }
  };

  useEffect(() => {
    if (data && data.siteDiary) {
      setStaffOnSiteData(mapGetSiteDiaryResponseModelToStaffOnSiteProps(data, currentHwrcSiteId, convertToLocalDate(currentDiaryDate)));
      setAuthorisedVisitorsTemporaryWorkers(
        mapGetSiteDiaryResponseModelToAuthorisedVisitorsTemporaryWorkers(data, diarySaved, convertToLocalDate(currentDiaryDate), currentHwrcSiteId)
      );
      setAuthorisedMaintenanceData(
        mapGetSiteDiaryResponseModelToAuthorisedMaintenance(data, diarySaved, convertToLocalDate(currentDiaryDate), currentHwrcSiteId)
      );
      setItemOrders(mapSiteDiaryItemOrdersToSiteDiaryItemOrderViewModels(data, diarySaved, convertToLocalDate(currentDiaryDate), currentHwrcSiteId));
      setEventsIncidents(mapSiteDiaryEventsAndIncidentsToViewModels(data, diarySaved, convertToLocalDate(currentDiaryDate), currentHwrcSiteId));

      navigate(`${RoutePathsEnum.SiteDiaryLandingPage}/${currentHwrcSiteId}/${convertToLocalDate(currentDiaryDate)}`);
    }
  }, [data]);

  useEffect(() => {
    if (data) {
      const blankDiary = { ...emptySiteDiary };
      blankDiary.isEmpty = false;
      const diary = data.siteDiary ?? blankDiary;

      if (data.siteDiary) {
        setDiarySaved(true);
      } else {
        setDiarySaved(false);
      }

      setSiteDiary(mapSiteDiaryToViewModel(diary));
    }
  }, [data]);

  useEffect(() => {
    if (statusCode === 401) navigate(RoutePathsEnum.Unauthorised);
  }, [statusCode]);

  const exceptionAlertCloseHandler = useCallback(() => {
    setShowExceptions(false);
  }, [setShowExceptions]);

  const diarySavedHandler = useCallback(
    (hwrcSiteId: string, selectedDate: Date) => {
      setStaffOnSiteData(emptyStaffOnSite);
      actions.updateSiteDiarySelectionDetails({
        hwrcSiteId,
        selectedDate
      });
      setDiarySaved(true);
    },
    [setDiarySaved, actions, setStaffOnSiteData]
  );

  return (
    <Page>
      {showExceptions && errors && (
        <AmplifyError
          errors={errors}
          isDismissable
          onCloseHandler={exceptionAlertCloseHandler}
        />
      )}
      <Card className="site-diary">
        <Card.Header>
          <h2>
            Site Diary
            <Spinner
              animation="grow"
              visible={isSending}
              spinnerText="Loading"
              variant="info"
            />
          </h2>
        </Card.Header>
        <Card.Body className="no-padding">
          {statusCode !== 400
          && (
            <>
              <Row>
                <Col xs={12} xl={6}>
                  <div className="site-diary-component-wrapper">
                    {siteDiary && (
                      <SiteDetails
                        siteDiary={siteDiary}
                        diarySave={diarySavedHandler}
                        diaryLoad={diaryLoadHandler}
                        hwrcSiteIdOverride={currentHwrcSiteId}
                        selectedDateOverride={currentDiaryDate}
                        onHwrcSiteChanged={(hwrcSiteId:string) => setCurrentHwrcSiteId(hwrcSiteId)}
                        onSelectedDateChanged={(selectedDate : Date) => setCurrentDiaryDate(selectedDate)}
                      />
                    ) }
                  </div>
                </Col>
                <Col xs={12} xl={6}>
                  <div className="site-diary-component-wrapper">
                    <StaffOnSite
                      staffOnSite={staffOnSiteData?.staffOnSite}
                      diaryDate={convertToLocalDate(currentDiaryDate)}
                      hwrcSiteId={currentHwrcSiteId}
                      diarySaved={diarySaved}
                    />
                  </div>
                </Col>
              </Row>
              <Row>
                <Col xs={12} xl={6}>
                  <div className="site-diary-component-wrapper">
                    <AuthorisedVisitorsTemporaryWorkers
                      diarySaved={diarySaved}
                      diaryDate={convertToLocalDate(currentDiaryDate)}
                      hwrcSiteId={currentHwrcSiteId}
                      authorisedVisitorsTemporaryWorkersDetails={authorisedVisitorsTemporaryWorkersData?.authorisedVisitorsTemporaryWorkersDetails}
                    />
                  </div>
                </Col>
                <Col xs={12} xl={6}>
                  <div className="site-diary-component-wrapper">
                    <EventsAndIncidents
                      diarySaved={diarySaved}
                      diaryDate={convertToLocalDate(currentDiaryDate)}
                      hwrcSiteId={currentHwrcSiteId}
                      eventsAndIncidents={eventsIncidents?.eventsAndIncidents}
                    />
                  </div>
                </Col>
              </Row>
              <Row>
                <Col xs={12} xl={6}>
                  <div className="site-diary-component-wrapper">
                    <AuthorisedMaintenance
                      diarySaved={diarySaved}
                      diaryDate={convertToLocalDate(currentDiaryDate)}
                      hwrcSiteId={currentHwrcSiteId}
                      authorisedMaintenanceDetails={authorisedMaintenanceData.authorisedMaintenanceDetails}
                    />
                  </div>
                </Col>
                <Col xs={12} xl={6}>
                  <div className="site-diary-component-wrapper">
                    <SiteDiaryItemOrders
                      diaryDate={convertToLocalDate(currentDiaryDate)}
                      diarySaved={diarySaved}
                      hwrcSiteId={currentHwrcSiteId}
                      itemOrders={itemOrders.itemOrders}
                    />
                  </div>
                </Col>
              </Row>
            </>
          )}
        </Card.Body>
      </Card>
    </Page>
  );
};

export default SiteDiaryLandingPage;
